// Detects durations in recipe instruction text, e.g. "20 minutes", "1-2 hours",
// "30 to 40 mins". Shared between the instruction highlighter and the timer feature.
export const DURATION_PATTERN =
  /\b\d+(?:\s*[-–to]+\s*\d+)?\s*(?:seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/gi

// Parses a matched duration into seconds. For ranges ("20-25 minutes") the lower
// bound is used so the cook checks the food before it overcooks. Returns null when
// no duration can be parsed.
export function parseDurationToSeconds(text: string): number | null {
  const match =
    /(\d+)(?:\s*[-–to]+\s*\d+)?\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?)/i.exec(
      text,
    )
  if (!match) return null

  const value = parseInt(match[1], 10)
  const unit = match[2].toLowerCase()
  const multiplier = unit.startsWith("h") ? 3600 : unit.startsWith("m") ? 60 : 1
  const seconds = value * multiplier

  return seconds > 0 ? seconds : null
}

// Extracts the sentence containing the [start, end) span, so a timer started
// from a duration can show what it was for (e.g. "simmer for 20 minutes until
// reduced"). Falls back to the whole text when no sentence breaks surround it.
export function extractSentence(
  text: string,
  start: number,
  end: number,
): string {
  const lastBreak = text.slice(0, start).search(/[.!?][^.!?]*$/)
  const sentenceStart = lastBreak === -1 ? 0 : lastBreak + 1
  const nextBreak = text.slice(end).search(/[.!?]/)
  const sentenceEnd = nextBreak === -1 ? text.length : end + nextBreak + 1
  return text.slice(sentenceStart, sentenceEnd).trim()
}

// Formats a remaining-seconds count as "M:SS" under an hour, "H:MM:SS" at or above.
export function formatSeconds(total: number): string {
  const clamped = Math.max(0, Math.floor(total))
  const hours = Math.floor(clamped / 3600)
  const minutes = Math.floor((clamped % 3600) / 60)
  const seconds = clamped % 60
  const pad = (n: number) => n.toString().padStart(2, "0")

  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`
  return `${minutes}:${pad(seconds)}`
}
